import {
  Button as AriaButton,
  Select as AriaSelect,
  SelectValue as AriaSelectValue,
  Key,
  ListBoxItemRenderProps,
} from 'react-aria-components';
import { Typography } from '../../atoms/typography/typography';
import { twMerge } from 'tailwind-merge';
import { InputVariants, input } from '../../patterns/input';
import { Label } from '../../atoms/label/label';
import { ComponentProps, ReactElement } from 'react';
import { IconAngleDown } from '@maia/icons';
import { SelectDropdown } from '../selectDropdown/selectDropdown';

type SelectItem<T> = {
  label: string;
  value: Key;
  meta: T;
};

type Props<T> = {
  items: Iterable<SelectItem<T>>;
  label?: string;
  value?: Key;
  enforceMinWidth?: boolean;
  enforceMaxHeight?: boolean;
  onChange: (selection: Key) => void;
  renderSelectedValue?: (props: SelectItem<T>) => ReactElement | null;
  renderItem?: (
    _: SelectItem<T>,
    itemInfo: ListBoxItemRenderProps,
  ) => ReactElement | null;
} & Omit<ComponentProps<typeof AriaSelect>, 'onSelectionChange' | 'onChange'> &
  InputVariants;

export function Select<T>(props: Props<T>) {
  const {
    label,
    items,
    size,
    value,
    enforceMinWidth,
    enforceMaxHeight,
    onChange,
    renderSelectedValue,
    renderItem,
    placeholder,
    ...rest
  } = props;

  return (
    <AriaSelect selectedKey={value} onSelectionChange={onChange} {...rest}>
      {label && <Label>{label}</Label>}

      <AriaButton className={twMerge(input({ size }))}>
        <AriaSelectValue className="flex items-center justify-between gap-x-12 w-full">
          {(selectedValue) => (
            <>
              {selectedValue.isPlaceholder || !renderSelectedValue ? (
                <Typography variant="body-xs" weight="regular" useCase="button">
                  {selectedValue.selectedText ?? placeholder}
                </Typography>
              ) : (
                renderSelectedValue(selectedValue.selectedItem as SelectItem<T>)
              )}
              <span className="ml-auto" aria-hidden="true">
                <IconAngleDown />
              </span>
            </>
          )}
        </AriaSelectValue>
      </AriaButton>

      <SelectDropdown
        items={items}
        renderOption={renderItem}
        enforceMinWidth={enforceMinWidth}
        enforceMaxHeight={enforceMaxHeight}
      />
    </AriaSelect>
  );
}
